import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { api, formatApiError } from '../lib/api';
import { formatPartialDate } from './PartialDateInput';
import {
  MapPin, CalendarDays, Clock, Phone, Globe, PenSquare,
  ChevronDown, ChevronUp, Check, Heart,
} from 'lucide-react';
import { toast } from 'sonner';

/* iter96 — Public memorial experience sections:
   QuickNav · AboutCard · UpcomingServices (+RSVP) · TributesSection ·
   FuneralHomeCard · CreateMemorialCTA */

const mapsUrl = (q) =>
  `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(q)}`;

export const EVENT_TYPE_LABELS = {
  visitation: 'Visitation / Viewing',
  funeral: 'Funeral Service',
  celebration_of_life: 'Celebration of Life',
  burial: 'Burial / Cemetery',
  reception: 'Reception',
  custom: 'Service Event',
};

/* ---------------- Quick navigation (sticky) ---------------- */
export function QuickNav({ sections }) {
  if (!sections || sections.length === 0) return null;
  const go = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
  return (
    <nav
      className="sticky top-[94px] z-20 bg-[hsl(var(--background))]/92 backdrop-blur-md border-y border-[hsl(var(--primary)/0.08)]"
      data-testid="memorial-quick-nav"
      aria-label="Memorial sections"
    >
      <div className="ml-container flex items-center justify-start sm:justify-center gap-1 overflow-x-auto py-2">
        {sections.map((s) => (
          <button
            key={s.id}
            type="button"
            onClick={() => go(s.id)}
            data-testid={`quick-nav-${s.id}`}
            className="shrink-0 px-4 py-2.5 rounded-full text-[11px] tracking-[0.18em] uppercase font-semibold text-[hsl(var(--primary)/0.65)] hover:text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary)/0.06)] transition-colors"
          >
            {s.label}
          </button>
        ))}
      </div>
    </nav>
  );
}

/* ---------------- About summary card ---------------- */
function computeAge(dob, dod) {
  if (!dob || !dod) return null;
  const b = new Date(dob);
  const d = new Date(dod);
  if (Number.isNaN(b.getTime()) || Number.isNaN(d.getTime())) {
    const by = parseInt(String(dob).slice(0, 4), 10);
    const dy = parseInt(String(dod).slice(0, 4), 10);
    if (by && dy && dy >= by) return dy - by;
    return null;
  }
  let age = d.getFullYear() - b.getFullYear();
  const m = d.getMonth() - b.getMonth();
  if (m < 0 || (m === 0 && d.getDate() < b.getDate())) age -= 1;
  return age >= 0 ? age : null;
}

export function AboutCard({ memorial }) {
  const firstName = (memorial.full_name || '').trim().split(/\s+/)[0];
  const age = computeAge(memorial.date_of_birth, memorial.date_of_passing);
  const items = [
    memorial.date_of_birth && { label: 'Born', value: formatPartialDate(memorial.date_of_birth) },
    memorial.date_of_passing && { label: 'Passed', value: formatPartialDate(memorial.date_of_passing) },
    age != null && { label: 'Age', value: `${age} years` },
    (memorial.hometown || memorial.birthplace) && { label: 'Hometown', value: memorial.hometown || memorial.birthplace },
    memorial.occupation && { label: 'Occupation', value: memorial.occupation },
    memorial.family_role && { label: 'Family role', value: memorial.family_role },
  ].filter(Boolean);
  if (items.length === 0) return null;
  return (
    <section className="ml-container pt-2 pb-4" data-testid="public-about-card">
      <div className="mx-auto max-w-3xl rounded-2xl border border-[hsl(var(--primary)/0.12)] bg-[hsl(var(--surface))] px-7 py-7 sm:px-9 sm:py-8">
        <div className="ml-overline text-center">About {firstName}</div>
        <div className="mt-5 grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-5 text-center">
          {items.map((it) => (
            <div key={it.label} data-testid={`about-item-${it.label.toLowerCase().replace(/\s+/g, '-')}`}>
              <div className="text-[10px] tracking-[0.24em] uppercase font-semibold text-[hsl(var(--primary)/0.5)]">
                {it.label}
              </div>
              <div className="font-serif text-lg text-[hsl(var(--primary))] mt-1 leading-snug">
                {it.value}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------------- Upcoming Services + RSVP ---------------- */
function ServiceRsvp({ slug, service }) {
  const [choice, setChoice] = useState(null);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null); // status string when submitted

  const OPTIONS = [
    { id: 'attending', label: 'Attending' },
    { id: 'maybe', label: 'Maybe' },
    { id: 'declined', label: 'Unable to Attend' },
  ];

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error('Please add your name so the family knows who is coming.');
      return;
    }
    setBusy(true);
    try {
      await api.post(`/public/memorials/${slug}/services/${service.service_id}/rsvp`, {
        status: choice,
        name: name.trim(),
        email: email.trim() || null,
      });
      setDone(choice);
    } catch (err) {
      toast.error(formatApiError(err, 'Could not send your RSVP. Please try again.'));
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div
        className="mt-5 pt-4 border-t border-[hsl(var(--primary)/0.1)] flex items-center gap-2 text-sm text-[hsl(var(--primary))]"
        data-testid={`rsvp-done-${service.service_id}`}
      >
        <Check className="w-4 h-4 text-[hsl(var(--accent))]" />
        Thank you — your RSVP has been shared with the family.
      </div>
    );
  }

  return (
    <div className="mt-5 pt-4 border-t border-[hsl(var(--primary)/0.1)]" data-testid={`rsvp-block-${service.service_id}`}>
      <div className="text-[11px] tracking-[0.22em] uppercase font-semibold text-[hsl(var(--primary)/0.6)]">
        Will you be attending?
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        {OPTIONS.map((o) => (
          <button
            key={o.id}
            type="button"
            onClick={() => setChoice(choice === o.id ? null : o.id)}
            data-testid={`rsvp-option-${o.id}-${service.service_id}`}
            className={`px-4 py-2.5 rounded-full text-[12px] font-semibold tracking-[0.04em] border transition-colors ${
              choice === o.id
                ? 'border-[hsl(var(--accent))] bg-[hsl(var(--accent))]/12 text-[hsl(var(--primary))]'
                : 'border-[hsl(var(--primary)/0.18)] text-[hsl(var(--primary)/0.75)] hover:border-[hsl(var(--accent))]'
            }`}
          >
            {o.label}
          </button>
        ))}
      </div>
      {choice && (
        <form onSubmit={submit} className="mt-4 flex flex-col sm:flex-row gap-2" data-testid={`rsvp-form-${service.service_id}`}>
          <input
            className="ml-input flex-1"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Your name"
            maxLength={80}
            data-testid={`rsvp-name-${service.service_id}`}
          />
          <input
            type="email"
            className="ml-input flex-1"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Email (optional)"
            maxLength={160}
            data-testid={`rsvp-email-${service.service_id}`}
          />
          <button
            type="submit"
            disabled={busy}
            className="ml-btn-gold !py-3"
            data-testid={`rsvp-submit-${service.service_id}`}
          >
            {busy ? 'Sending…' : 'Send RSVP'}
          </button>
        </form>
      )}
    </div>
  );
}

function ServiceCard({ slug, service }) {
  const typeLabel = EVENT_TYPE_LABELS[service.event_type] || EVENT_TYPE_LABELS.custom;
  const dateLabel = service.date
    ? new Date(`${service.date}T12:00:00`).toLocaleDateString(undefined, {
        weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
      })
    : null;
  const isPast = service.date && service.date < new Date().toISOString().slice(0, 10);
  const directionsQ = [service.location_name, service.address].filter(Boolean).join(', ');

  return (
    <div
      className="rounded-2xl border border-[hsl(var(--primary)/0.12)] bg-[hsl(var(--background))] shadow-[0_18px_48px_-36px_rgba(26,54,38,0.5)] p-6 sm:p-8"
      data-testid={`service-card-${service.service_id}`}
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="text-[10px] tracking-[0.28em] uppercase font-semibold text-[hsl(var(--accent))]">
          {typeLabel}
        </div>
        {isPast && (
          <span className="text-[10px] tracking-[0.2em] uppercase font-semibold text-[hsl(var(--primary)/0.4)] border border-[hsl(var(--primary)/0.15)] rounded-full px-2.5 py-1">
            Past
          </span>
        )}
      </div>
      <h3 className="font-serif text-2xl sm:text-3xl text-[hsl(var(--primary))] mt-2 leading-snug" data-testid={`service-name-${service.service_id}`}>
        {service.name}
      </h3>
      <div className="mt-4 space-y-2.5 text-[15px] text-[hsl(var(--primary)/0.85)]">
        {dateLabel && (
          <div className="flex items-center gap-2.5" data-testid={`service-date-${service.service_id}`}>
            <CalendarDays className="w-4 h-4 text-[hsl(var(--accent))] shrink-0" /> {dateLabel}
          </div>
        )}
        {service.time && (
          <div className="flex items-center gap-2.5" data-testid={`service-time-${service.service_id}`}>
            <Clock className="w-4 h-4 text-[hsl(var(--accent))] shrink-0" /> {service.time}
          </div>
        )}
        {(service.location_name || service.address) && (
          <div className="flex items-start gap-2.5" data-testid={`service-location-${service.service_id}`}>
            <MapPin className="w-4 h-4 text-[hsl(var(--accent))] shrink-0 mt-1" />
            <div>
              {service.location_name && (
                <div className="font-medium text-[hsl(var(--primary))]">{service.location_name}</div>
              )}
              {service.address && (
                <div className="text-[14px] text-[hsl(var(--primary)/0.7)]">{service.address}</div>
              )}
            </div>
          </div>
        )}
      </div>
      {service.notes && (
        <p className="mt-4 text-sm text-[hsl(var(--primary)/0.7)] leading-relaxed italic" data-testid={`service-notes-${service.service_id}`}>
          {service.notes}
        </p>
      )}
      {directionsQ && (
        <div className="mt-5">
          <a
            href={mapsUrl(directionsQ)}
            target="_blank"
            rel="noreferrer"
            className="ml-btn-gold inline-flex items-center gap-2"
            data-testid={`service-directions-${service.service_id}`}
          >
            <MapPin className="w-3.5 h-3.5" /> Get Directions
          </a>
        </div>
      )}
      {service.rsvp_enabled !== false && <ServiceRsvp slug={slug} service={service} />}
    </div>
  );
}

export function UpcomingServices({ services, slug }) {
  if (!services || services.length === 0) return null;
  return (
    <section id="services" className="ml-section ml-container scroll-mt-40" data-testid="public-services">
      <div className="text-center mb-12">
        <div className="ml-overline">Honoring their life</div>
        <h2 className="font-serif font-light text-3xl sm:text-4xl text-[hsl(var(--primary))] mt-5 tracking-tight">
          Upcoming Services
        </h2>
      </div>
      <div className="max-w-3xl mx-auto space-y-5">
        {services.map((s) => (
          <ServiceCard key={s.service_id} slug={slug} service={s} />
        ))}
      </div>
    </section>
  );
}

/* ---------------- Tributes section ---------------- */
function TributeCard({ t }) {
  return (
    <div
      className="rounded-2xl border border-[hsl(var(--primary)/0.12)] bg-[hsl(var(--background))] p-6 sm:p-7 text-left"
      data-testid={`public-tribute-${t.tribute_id}`}
    >
      <div className="flex items-baseline justify-between gap-3 flex-wrap">
        <div>
          <span className="font-serif text-xl text-[hsl(var(--primary))]">{t.name}</span>
          {t.relationship && (
            <span className="text-sm text-[hsl(var(--primary)/0.55)] italic ml-2">· {t.relationship}</span>
          )}
        </div>
        {t.created_at && (
          <span className="text-[10px] tracking-[0.18em] uppercase text-[hsl(var(--primary)/0.4)]">
            {new Date(t.created_at).toLocaleDateString()}
          </span>
        )}
      </div>
      <p className="mt-3 text-[15px] text-[hsl(var(--primary)/0.85)] leading-relaxed font-light whitespace-pre-line">
        {t.message}
      </p>
      {t.video_url && (
        <video
          src={t.video_url}
          controls
          playsInline
          preload="metadata"
          className="mt-4 w-full max-w-md rounded-lg bg-black"
          style={{ maxHeight: 300 }}
          data-testid={`public-tribute-video-${t.tribute_id}`}
        />
      )}
      {t.family_reply && (
        <div
          className="mt-4 ml-3 sm:ml-5 pl-4 border-l-2 border-[hsl(var(--accent))]"
          data-testid={`public-tribute-reply-${t.tribute_id}`}
        >
          <div className="text-[10px] tracking-[0.22em] uppercase font-semibold text-[hsl(var(--accent))]">
            Response from the family
          </div>
          <p className="mt-1.5 text-sm text-[hsl(var(--primary)/0.8)] leading-relaxed font-light whitespace-pre-line">
            {t.family_reply}
          </p>
        </div>
      )}
    </div>
  );
}

export function TributesSection({ slug, memorialName, tributeCount, recentTributes }) {
  const [all, setAll] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(false);

  const loadAll = async () => {
    if (all) { setExpanded(true); return; }
    setLoading(true);
    try {
      const { data } = await api.get(`/public/memorials/${slug}/tributes`);
      setAll(data.tributes || []);
      setExpanded(true);
    } catch (e) {
      toast.error(formatApiError(e, 'Could not load tributes'));
    } finally {
      setLoading(false);
    }
  };

  const shown = expanded && all ? all : (recentTributes || []);
  const firstName = (memorialName || '').trim().split(/\s+/)[0];

  return (
    <section id="tributes" className="ml-section ml-container scroll-mt-32" data-testid="public-tributes">
      <div className="text-center mb-10">
        <div className="ml-overline">Tributes</div>
        <h2 className="font-serif font-light text-3xl sm:text-4xl text-[hsl(var(--primary))] mt-5 tracking-tight">
          Memories shared
        </h2>
        <div className="mt-3 text-[12px] tracking-[0.22em] uppercase font-semibold text-[hsl(var(--primary)/0.5)]" data-testid="tribute-count">
          {tributeCount > 0
            ? `${tributeCount} ${tributeCount === 1 ? 'tribute' : 'tributes'} shared`
            : 'No tributes yet'}
        </div>
        <div className="mt-7">
          <Link
            to={`/m/${slug}/memory`}
            className="ml-btn-gold inline-flex items-center gap-2 !px-8 !py-4"
            data-testid="leave-tribute-btn"
          >
            <PenSquare className="w-4 h-4" /> Leave a Tribute
          </Link>
        </div>
      </div>

      {shown.length === 0 ? (
        <p className="text-center font-serif italic text-[16px] text-[hsl(var(--primary)/0.6)]" data-testid="tributes-empty">
          Be the first to share a memory of {firstName}.
        </p>
      ) : (
        <div className="max-w-2xl mx-auto space-y-4">
          {shown.map((t) => <TributeCard key={t.tribute_id} t={t} />)}
        </div>
      )}

      {!expanded && tributeCount > (recentTributes || []).length && (
        <div className="text-center mt-7">
          <button
            type="button"
            onClick={loadAll}
            disabled={loading}
            className="inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase font-semibold text-[hsl(var(--accent))] hover:text-[hsl(var(--primary))] transition-colors"
            data-testid="tributes-view-all"
          >
            <ChevronDown className="w-4 h-4" />
            {loading ? 'Loading…' : `View all ${tributeCount} tributes`}
          </button>
        </div>
      )}
      {expanded && (
        <div className="text-center mt-7">
          <button
            type="button"
            onClick={() => setExpanded(false)}
            className="inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase font-semibold text-[hsl(var(--primary)/0.55)] hover:text-[hsl(var(--primary))] transition-colors"
            data-testid="tributes-show-fewer"
          >
            <ChevronUp className="w-4 h-4" /> Show fewer
          </button>
        </div>
      )}
    </section>
  );
}

/* ---------------- Funeral home partnership card ---------------- */
export function FuneralHomeCard({ memorial }) {
  if (!memorial.funeral_home_enabled || !memorial.funeral_home_name) return null;
  const website = memorial.funeral_home_website
    ? (memorial.funeral_home_website.startsWith('http')
        ? memorial.funeral_home_website
        : `https://${memorial.funeral_home_website}`)
    : null;
  return (
    <section className="ml-container pt-12 pb-2" data-testid="public-funeral-home-card">
      <div className="mx-auto max-w-3xl rounded-2xl border border-[hsl(var(--primary)/0.12)] bg-[hsl(var(--surface))] px-7 py-7 sm:px-9 sm:py-8">
        <div className="ml-overline text-center">Services entrusted to</div>
        <div className="mt-5 flex flex-col sm:flex-row items-center gap-5 sm:gap-7">
          {memorial.funeral_home_logo_url && (
            <img
              src={memorial.funeral_home_logo_url}
              alt={memorial.funeral_home_name}
              className="w-20 h-20 rounded-lg object-contain bg-white border border-[hsl(var(--primary)/0.1)] p-1.5 shrink-0"
              data-testid="funeral-home-logo"
            />
          )}
          <div className="flex-1 text-center sm:text-left">
            <div className="font-serif text-2xl text-[hsl(var(--primary))]" data-testid="funeral-home-name">
              {memorial.funeral_home_name}
            </div>
            {memorial.funeral_home_address && (
              <div className="text-sm text-[hsl(var(--primary)/0.65)] mt-1">
                {memorial.funeral_home_address}
              </div>
            )}
            <div className="mt-4 flex flex-wrap justify-center sm:justify-start gap-2.5">
              {memorial.funeral_home_phone && (
                <a
                  href={`tel:${memorial.funeral_home_phone.replace(/[^+\d]/g, '')}`}
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-[hsl(var(--primary)/0.18)] text-[12px] font-semibold text-[hsl(var(--primary))] hover:border-[hsl(var(--accent))] transition-colors"
                  data-testid="funeral-home-phone"
                >
                  <Phone className="w-3.5 h-3.5" /> {memorial.funeral_home_phone}
                </a>
              )}
              {website && (
                <a
                  href={website}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-[hsl(var(--primary)/0.18)] text-[12px] font-semibold text-[hsl(var(--primary))] hover:border-[hsl(var(--accent))] transition-colors"
                  data-testid="funeral-home-website"
                >
                  <Globe className="w-3.5 h-3.5" /> Website
                </a>
              )}
              {memorial.funeral_home_address && (
                <a
                  href={mapsUrl(`${memorial.funeral_home_name}, ${memorial.funeral_home_address}`)}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-[hsl(var(--primary)/0.18)] text-[12px] font-semibold text-[hsl(var(--primary))] hover:border-[hsl(var(--accent))] transition-colors"
                  data-testid="funeral-home-directions"
                >
                  <MapPin className="w-3.5 h-3.5" /> Directions
                </a>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------------- Bottom CTA — Preserve Your Family Story ---------------- */
export function CreateMemorialCTA() {
  const features = [
    'Photos & videos',
    'Life stories',
    'Family memories',
    'Audio narration',
    'QR memorial access',
  ];
  return (
    <section
      className="ml-section py-16 sm:py-20 mt-12"
      style={{ backgroundColor: '#1A3626' }}
      data-testid="public-create-cta"
    >
      <div className="ml-container text-center">
        <Heart className="w-6 h-6 mx-auto text-[#D9B775]" strokeWidth={1.5} />
        <h2 className="font-serif font-light text-3xl sm:text-4xl text-[#F4E5C2] mt-5 tracking-tight">
          Preserve Your Family Story
        </h2>
        <p className="mt-4 text-[15px] text-[#F4E5C2]/80 max-w-md mx-auto leading-relaxed font-light">
          Create a lasting memorial for someone you love — kept safe for the
          funeral week, the first year of remembrance, and every generation after.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-x-5 gap-y-2">
          {features.map((f) => (
            <span key={f} className="text-[11px] tracking-[0.2em] uppercase font-semibold text-[#D9B775]">
              {f}
            </span>
          ))}
        </div>
        <Link
          to="/register"
          className="mt-9 inline-flex items-center gap-2 px-9 py-4 rounded-md text-[12px] tracking-[0.18em] uppercase font-semibold bg-[#C5A059] text-[#1A3626] hover:bg-[#D9B775] transition-colors"
          data-testid="create-memorial-cta-btn"
        >
          Create Your Memorial
        </Link>
      </div>
    </section>
  );
}
